/**
 * 인증 토큰이 포함된 전역 fetch wrapper
 * 401 에러 시 자동 로그아웃 처리
 */

// 401 오류 시 자동 로그아웃 처리 함수
const handle401Error = () => {
  console.error('🚨 401 인증 오류 감지 - 세션 만료로 자동 로그아웃 처리');

  // 1. localStorage에서 JWT 토큰 제거
  localStorage.removeItem('adminToken');

  // 2. Zustand persist 스토리지 제거 (로그인 상태 초기화)
  localStorage.removeItem('bus-auth-storage');

  // 3. 세션 만료 플래그 설정 (로그인 페이지에서 메시지 표시용)
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem('session_expired', 'true');
  }

  // 4. 사용자에게 알림
  if (typeof window !== 'undefined') {
    console.warn('⚠️ 세션이 만료되었습니다. 다시 로그인해주세요.');

    // 5. 페이지 새로고침 (LoginApp에서 isAuthenticated가 false가 되어 로그인 페이지로 이동)
    window.location.reload();
  }
};

/**
 * 인증 토큰이 포함된 fetch 래퍼
 * 401 에러 발생 시 자동으로 로그아웃 처리
 */
export const fetchWithAuth = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  // 1. localStorage에서 JWT 토큰 가져오기
  const token = localStorage.getItem('adminToken');

  // 2. Authorization 헤더 추가
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // 3. fetch 요청
  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // 4. 401 에러 체크
    if (response.status === 401) {
      console.error(`🚨 401 Unauthorized: ${url}`);
      handle401Error();
      throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
    }

    return response;
  } catch (error) {
    // 네트워크 에러 등 다른 에러는 그대로 throw
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('네트워크 연결 오류: 서버에 연결할 수 없습니다.');
    }
    throw error;
  }
};

/**
 * JSON 응답을 기대하는 fetch 래퍼
 */
export const fetchJsonWithAuth = async <T = any>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  const response = await fetchWithAuth(url, options);

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.message || errorMessage;
    } catch {
      // JSON 파싱 실패 시 기본 메시지 사용
    }

    throw new Error(errorMessage);
  }

  return response.json();
};

export default fetchWithAuth;
